/** ABM industry pages — approved copy from abmparking.com/industries, reproduced verbatim. */
import type { AbmDetailPage, AbmIndexIntro } from './abm-content.types';

export const ABM_INDUSTRIES_INTRO: AbmIndexIntro = {
  title: 'Industries We Serve',
  metaTitle: 'Industries',
  text: 'Elevating premier properties with industry-leading parking management that delivers value, service, and performance.',
  heroImage: 'v2-contact-header.webp',
};

const SHUTTLE_CTA = { label: 'Learn More', href: '/services/shuttle-services' };
const VALET_CTA = { label: 'Learn More', href: '/services/valet-parking' };
const caseStudy = (href: string) => ({ label: 'Read Case Study', href, external: true });

export const ABM_INDUSTRIES: AbmDetailPage[] = [
  {
    slug: 'aviation',
    title: 'Aviation',
    summary:
      'ABM Aviation delivers integrated parking and transportation solutions that keep airports moving efficiently and passengers moving smoothly.',
    icon: 'industry-aviation.svg',
    heroImage: 'v2-aviation-header.webp',
    cardImage: 'v2-aviation-airport-parking-transportation-services.webp',
    sections: [
      {
        heading: 'Airport parking and transportation services',
        blocks: [
          {
            kind: 'text',
            text: 'At ABM Industries, we understand the critical role that seamless parking and transportation solutions play in the efficient operation of airports. Our executive leaders have over 120 years of combined experience. This level of experience combined with an unwavering commitment to excellence offers clients a comprehensive suite of services tailored to meet the unique needs of airport facilities, ensuring a smooth and hassle-free experience for travelers, employees, and partners alike. As we say, we have you covered, “From the car seat to the plane seat”.',
          },
        ],
        image: 'v2-aviation-airport-parking-transportation-services.webp',
      },
      {
        heading: 'Parking management excellence',
        blocks: [
          {
            kind: 'text',
            text: 'Our airport parking management solutions are designed to optimize space utilization, enhance traffic flow, and maximize revenue generation. Through innovative technologies and strategic planning, we help airports efficiently manage their parking assets, providing convenient and secure parking options for passengers and visitors.',
          },
        ],
        image: 'v2-aviation-parking-management-excellence.webp',
      },
      {
        heading: 'Shuttle services',
        blocks: [
          {
            kind: 'text',
            text: 'ABM Aviation operates over 800 shuttles for airports. We offer reliable and efficient shuttle services that connect airport terminals, parking facilities, rental car centers, and other key locations. Our shuttle services are designed to enhance passenger convenience, reduce congestion, and provide a seamless travel experience for all airport users. Our electric shuttles not only reduce carbon emissions but also provide a cost-effective alternative for your transportation needs. With ABM, you can expect innovative options and cutting edge solutions that contribute to a more sustainable future while ensuring safe and reliable transportation for your customers.',
          },
        ],
        image: 'v2-aviation-shuttle-services.webp',
        ctas: [SHUTTLE_CTA],
      },
      {
        heading: 'Valet parking',
        blocks: [
          {
            kind: 'text',
            text: 'Our valet parking services offer premium parking experience for travelers, providing a convenient and efficient way to park their vehicles. Our professional valets ensure that each vehicle is parked safely and securely, allowing travelers to enjoy a stress-free parking experience.',
          },
        ],
        image: 'abm-v2-aviation-001.webp',
        ctas: [VALET_CTA],
      },
      {
        heading:
          'ABM’s partnership with Port Authority of New York and New Jersey (JFK, LGA, and EWR airports)',
        blocks: [
          {
            kind: 'text',
            text: 'ABM’s partnership with the Port Authority of New York and New Jersey (PANYNJ) spanning over two decades, demonstrates a steadfast commitment to optimizing parking operations, enhancing efficiencies, and maximizing revenue generation. ABM’s management teams stationed at John F. Kennedy International Airport (JFK), LaGuardia Airport (LGA), and Newark Liberty International Airport (EWR) bring extensive and in-depth knowledge to the table, ensuring a thorough understanding of the unique requirements and challenges faced by PANYNJ.',
          },
        ],
        image: 'v2-aviation-case-study-jfk-lga-ewr.webp',
        ctas: [caseStudy('https://brandcenter.abm.com/share/C6954462-A47B-429A-A46943EB27A9D92F/')],
      },
      {
        heading: 'ABM’s partnership with Los Angeles World Airports (LAX)',
        blocks: [
          {
            kind: 'text',
            text: 'ABM has been instrumental in advancing the parking offerings at Los Angeles World Airports (LAWA), turning LAX into a model of efficiency and innovation in landside operations. A key element of this transformation is the implementation of ABM Vantage’s Online Booking System (OBS), which enables travelers to plan ahead, reserve parking, and enjoy a seamless arrival experience.',
          },
          {
            kind: 'text',
            text: 'Powered by ABM Vantage – our Integrated Mobility Solution – this smart, digital-first platform connects to existing PARCS infrastructure and scales to incorporate additional technologies such as valet and EV services. By enabling pre-booking, dynamic pricing, and real-time data visibility, our system and professional services not only enhances the guest experience but also maximize revenue and operational efficiency.',
          },
        ],
        image: 'v2-aviation-case-study-lax.webp',
        ctas: [caseStudy('https://brandcenter.abm.com/share/4836B1BB-02AB-42BD-9C495184CB057B79/')],
      },
    ],
  },
  {
    slug: 'commercial-office',
    title: 'Commercial & Office',
    summary:
      'As a national leader in parking operations, ABM Parking delivers coast-to-coast solutions tailored for commercial properties, with the scale, systems, and experience to serve clients in every major U.S. market. As a national leader in parking operations, ABM Parking delivers coast-to-coast solutions for commercial properties, with the scale and expertise to support clients across the country.',
    icon: 'industry-commercial-office.svg',
    heroImage: 'v2-commercial-office-header.webp',
    cardImage: 'v2-commercial-office-driving-revenue.webp',
    sections: [
      {
        heading: 'Driving revenue. Elevating experience.',
        blocks: [
          {
            kind: 'text',
            text: 'With decades of proven performance, ABM manages garage operations for some of the most prestigious buildings and real estate portfolios, generating over $1.5 billion in annual parking revenue for our clients.',
          },
          {
            kind: 'text',
            text: 'Our approach is designed to maximize revenue and drive cost efficiency through:',
          },
          {
            kind: 'list',
            items: [
              'Data-driven strategies that optimize occupancy and pricing',
              'Advanced technology for streamlined payment, access control, and reporting',
              'Operational excellence that minimizes overhead and maximizes ROI',
              'Scalable solutions to support both single-site and multi-location portfolios',
              'A strong focus on customer service, safety, and satisfaction',
            ],
          },
          {
            kind: 'text',
            text: 'From East to West and everywhere in between, ABM is uniquely positioned to deliver reliable, high-performance parking management that boosts the bottom line while enhancing the user experience.',
          },
        ],
        image: 'v2-commercial-office-driving-revenue.webp',
      },
      {
        heading: 'Shuttle services',
        blocks: [
          {
            kind: 'text',
            text: 'ABM operates a fleet of over 1,000 shuttles, transporting over 47 million passengers annually. From corporate campuses and airports to hospitals, universities, and event venues, we deliver reliable, efficient transportation solutions that scale to your needs.',
          },
          {
            kind: 'text',
            text: 'With a strong emphasis on safety, punctuality, and comfort, our shuttles are maintained to the highest standards. Every driver is thoroughly vetted and professionally trained to provide courteous, helpful service—ensuring a positive experience from the first pickup to the final drop-off.',
          },
          {
            kind: 'text',
            text: 'Whether you’re moving employees across a commercial office park or managing transit at a large event, ABM’s shuttle services are built for performance, professionalism, and peace of mind.',
          },
        ],
        image: 'v2-commercial-office-shuttle-services-1.webp',
        ctas: [SHUTTLE_CTA],
      },
      {
        heading: 'Valet services',
        blocks: [
          {
            kind: 'text',
            text: 'ABM delivers premier valet and hospitality services for commercial, office, residential, hotels, and mixed-use properties, backed by decades of experience and a national footprint. We proudly serve prestigious clients across the country, including luxury office towers, corporate campuses, healthcare institutions, and iconic destination properties.',
          },
          {
            kind: 'text',
            text: 'Our valet services are designed to enhance the arrival experience through a perfect blend of professionalism, hospitality, and innovation. With a focus on customer satisfaction and operational efficiency, ABM sets the standard for valet excellence coast to coast.',
          },
          { kind: 'subheading', text: 'What Sets ABM Apart:' },
          {
            kind: 'list',
            items: [
              'Extensive valet and hospitality expertise across diverse environments',
              'A trusted partner for high-profile clients nationwide',
              'Highly trained staff delivering prompt, courteous, white-glove service',
              'Strategic operations that ensure safety, speed, and efficiency',
            ],
          },
        ],
        image: 'abm-v2-commerical-office-002.webp',
        ctas: [VALET_CTA],
      },
      {
        heading: 'Embracing technology for a better experience',
        blocks: [
          {
            kind: 'text',
            text: 'ABM integrates cutting-edge technology to streamline the valet process and enhance user satisfaction, including:',
          },
          {
            kind: 'list',
            items: [
              'Digital payment systems for fast, contactless transactions',
              'Real-time vehicle tracking and retrieval notifications',
              'Mobile-friendly platforms for scheduling, tipping, and customer feedback',
            ],
          },
          {
            kind: 'text',
            text: 'Our solutions not only maximize revenue and space utilization, but also deliver a modern, elevated valet experience that reflects the standards of today’s top-tier properties.',
          },
        ],
        image: 'abm-v2-commerical-office-003.webp',
      },
    ],
  },
  {
    slug: 'education',
    title: 'Education',
    summary:
      'With decades of experience in education, ABM delivers customized parking systems that improve flow, enhance convenience, and support campus operations—tailored to each institution’s unique needs.',
    icon: 'industry-education.svg',
    heroImage: 'v2-education-header.webp',
    cardImage: 'v2-education-optimized-parking.webp',
    sections: [
      {
        heading: 'Optimized parking for students, staff, and visitors',
        blocks: [
          {
            kind: 'text',
            text: 'ABM recognizes that no two campuses are the same. We tailor parking systems based on the size, layout, and traffic patterns of each educational institution. ABM provides customizable parking solutions tailored to each educational institution’s unique needs, enhancing efficiency and user satisfaction.',
          },
          { kind: 'text', text: 'This includes:' },
          {
            kind: 'list',
            items: [
              'Designating appropriate student, faculty, visitor, and ADA-accessible parking zones',
              'Creating flexible permit systems for semester, daily, or event-based use',
              'Implementing digital payments and real-time parking updates',
              'Optimizing space and signage in collaboration with campus administrators',
            ],
          },
        ],
        image: 'v2-education-optimized-parking.webp',
      },
      {
        heading: 'Enhancing mobility and convenience',
        blocks: [
          {
            kind: 'text',
            text: 'ABM’s comprehensive services support campus operations and include:',
          },
          {
            kind: 'list',
            items: [
              'Shuttle Services for better connectivity',
              'Full Parking Management (enforcement, maintenance, customer service)',
              '24/7 Call Center Support for immediate assistance',
            ],
          },
        ],
        image: 'v2-education-enhancing-mobility.webp',
      },
      {
        heading: 'Sustainable and cost-effective solutions',
        blocks: [
          {
            kind: 'text',
            text: 'ABM focuses on environmentally conscious practices that support academic priorities and operational efficiency.',
          },
        ],
        image: 'v2-education-sustainable-solutions.webp',
      },
    ],
  },
  {
    slug: 'healthcare-hospitals',
    title: 'Healthcare & Hospitals',
    summary:
      'ABM Parking is a proven partner for hospitals, medical centers, and healthcare campuses across the nation. We understand that in a healthcare environment, parking is more than just a convenience—it’s part of the care experience.',
    icon: 'industry-healthcare-hospitals.svg',
    heroImage: 'v2-healthcare-header.webp',
    cardImage: 'v2-healthcare-specialized-experience.webp',
    sections: [
      {
        heading: 'Specialized experience that makes a difference',
        blocks: [
          {
            kind: 'text',
            text: 'ABM Parking has partnered with leading healthcare institutions nationwide, managing complex, high-volume environments with precision and care. Our team understands:',
          },
          {
            kind: 'list',
            items: [
              'The importance of reducing patient stress through smooth arrivals and departures',
              'The need for ADA compliance and accessibility in every aspect of the parking journey',
              'How to support emergency, short-term, and long-term parking needs across a 24/7 operation',
              'The value of compassionate service in patient-facing roles like valet and shuttle drivers',
            ],
          },
          {
            kind: 'text',
            text: 'This level of insight allows us to deliver parking solutions that align with your facility’s mission of care and healing.',
          },
        ],
        image: 'v2-healthcare-specialized-experience.webp',
      },
      {
        heading: 'Our tailored healthcare parking programs include:',
        blocks: [
          {
            kind: 'list',
            items: [
              'Valet Services: Compassionate attendants trained to assist patients, including elderly and mobility-impaired visitors',
              'Parking Management: Advanced technologies like touchless payments and license plate recognition for safe, seamless entry and exit',
              'Shuttle Services: On-campus transit solutions to support large or multi-building facilities.',
              'Curbside Management: Organized drop-off/pick-up areas to maintain flow and safety.',
            ],
          },
          {
            kind: 'text',
            text: 'ABM Parking is more than a service provider—we’re an extension of your patient experience. Our knowledge, compassion, and operational excellence make us the trusted choice for healthcare institutions nationwide.',
          },
        ],
        image: 'v2-healthcare-tailored-parking.webp',
      },
      {
        heading: 'Why healthcare providers choose ABM',
        blocks: [
          {
            kind: 'list',
            items: [
              'National footprint with local expertise at hundreds of healthcare sites',
              'Decades of experience in the unique challenges of medical campus parking',
              'A focus on service, safety, and sensitivity in every patient interaction',
              'Custom solutions that reflect the operational and financial goals of each healthcare partner',
            ],
          },
          {
            kind: 'text',
            text: 'Our deep knowledge of healthcare operations allows us to design and manage parking programs that are efficient, compassionate, and patient-centered. From arrival to departure, ABM’s services are tailored to support the unique demands of clinical environments, where first impressions matter and every moment counts.',
          },
        ],
        image: 'v2-healthcare-healthcare-providers.webp',
      },
    ],
  },
  {
    slug: 'hospitality',
    title: 'Hospitality',
    summary:
      'At ABM, we deliver exceptional parking and hospitality solutions across a diverse range of industries, each with its own unique demands and guest expectations. Whether managing a luxury hotel entrance, a residential tower, a large-scale event, or a high-end retail destination, our teams provide seamless, white-glove service that elevates the customer experience from the very first interaction.',
    icon: 'industry-hospitality.svg',
    heroImage: 'v2-hospitality-header.webp',
    cardImage: 'v2-hospitality-luxury-parking.webp',
    sections: [
      {
        heading: 'Luxury parking that mirrors your guest experience',
        blocks: [
          {
            kind: 'text',
            text: 'Parking is the first impression your guests receive. ABM delivers luxury valet and parking management services that reflect your brand’s commitment to excellence—complete with technology integration, personalized greetings, and efficiency that sets the tone for a memorable stay.',
          },
        ],
        image: 'v2-hospitality-luxury-parking.webp',
      },
      {
        heading: 'Our commitment to white-glove service',
        blocks: [
          {
            kind: 'text',
            text: 'What truly sets ABM apart is our commitment to white-glove service across every sector. Our teams are trained to:',
          },
          {
            kind: 'list',
            items: [
              'Represent your brand with integrity and polish',
              'Greet guests with professionalism and warmth',
              'Offer personalized assistance and anticipate needs',
              'Defensive driving and safe vehicle handling techniques',
              'Damage prevention, vehicle inspection, and incident response procedures',
            ],
          },
          {
            kind: 'text',
            text: 'From parking to people skills, ABM’s service is designed to impress.',
          },
        ],
        image: 'v2-hospitality-our-commitment.webp',
      },
      {
        heading: 'Case Study: Hilton Long Beach',
        blocks: [
          {
            kind: 'text',
            text: 'ABM’s partnership with Hilton Long Beach has transformed their parking operations, resulting in improved service delivery, increased revenue, streamlined operations, and enhanced guest experiences. By focusing on staffing, advanced technology, seamless integration, and more, discover how ABM has elevated the overall parking experience for the hotel.',
          },
        ],
        image: 'v2-hospitality-hilton-long-beach.webp',
        ctas: [caseStudy('https://brandcenter.abm.com/share/ECFCDB05-E400-4634-9067949963D839E5/')],
      },
    ],
  },
  {
    slug: 'municipalities',
    title: 'Municipalities',
    summary:
      'With decades of experience partnering with cities and local governments, ABM Parking is a recognized leader in municipal parking management across the United States. From bustling urban centers to smaller communities, ABM has built a strong reputation for delivering efficient, community-focused parking solutions tailored to the unique needs of each municipality.',
    icon: 'industry-municipalities.svg',
    heroImage: 'v2-municipalities-header.webp',
    cardImage: 'v2-municipalities-parking-expertise-edit.webp',
    sections: [
      {
        heading: 'Municipal parking expertise that delivers real results',
        blocks: [
          {
            kind: 'text',
            text: 'Operating in municipal environments nationwide, ABM understands the complex challenges cities face—from budget constraints and evolving technology demands to balancing enforcement with constituent satisfaction. Our expertise is grounded in long-standing partnerships with local governments, delivering results that improve compliance, increase revenue, and enhance overall parking experience.',
          },
        ],
        image: 'v2-municipalities-parking-expertise-edit.webp',
      },
      {
        heading: 'Trusted parking management for municipal success',
        blocks: [
          {
            kind: 'text',
            text: 'ABM’s coast-to-coast presence allows us to bring national best practices to local markets. Scalable services that adapt to each city’s size, budget, and infrastructure Our municipal partnerships span decades, with ABM successfully managing parking operations for:',
          },
          {
            kind: 'list',
            items: [
              'Downtown districts',
              'Transit-oriented developments',
              'Public lots and garages',
              'On-street parking enforcement',
              'Special events and seasonal programs',
            ],
          },
        ],
        image: 'v2-municipalities-municipal-success.webp',
      },
      {
        heading: 'Parking that reflects community values',
        blocks: [
          {
            kind: 'text',
            text: 'At ABM, we believe that successful municipal parking programs are built on trust, transparency, and community involvement. Through open communication and close collaboration, we partner with local governments to develop strategies that reflect the needs and values of the communities they serve.',
          },
          {
            kind: 'text',
            text: 'We engage directly with city officials, stakeholders, and residents to ensure parking and transportation initiatives are:',
          },
          {
            kind: 'list',
            items: [
              'Transparent and inclusive',
              'Aligned with community priorities',
              'Designed to promote fairness and accessibility',
            ],
          },
          {
            kind: 'text',
            text: 'By fostering a sense of community ownership, ABM helps municipalities gain public support for parking programs that not only improve mobility but also contribute to the overall quality of life for residents.',
          },
        ],
        image: 'v2-municipalities-community-values.webp',
      },
      {
        heading: 'Case Study: City of Rockford',
        blocks: [
          {
            kind: 'text',
            text: 'Since 2013, ABM has transformed the City of Rockford’s parking operations—modernizing equipment, improving revenue, and enhancing the customer experience. With upgraded technology, 24/7 support, and seamless online tools, ABM helped Rockford boost efficiency and convenience across 8,400+ parking spaces.',
          },
        ],
        image: 'v2-municipalities-city-of-rockford.webp',
        ctas: [caseStudy('https://brandcenter.abm.com/share/C46C70A1-CE4F-466B-9A3AB423D2E3C163/')],
      },
    ],
  },
  {
    slug: 'residential',
    title: 'Residential',
    summary:
      'ABM offers a full suite of parking services for residential communities, designed to elevate the living experience for residents and guests alike. Whether at luxury condominiums, upscale apartment complexes, or high-rise residential towers, ABM delivers tailored, white-glove parking solutions that prioritize convenience, safety, and service excellence.',
    icon: 'industry-residential.svg',
    heroImage: 'v2-residential-header.webp',
    cardImage: 'v2-residential-exceptional-service.webp',
    sections: [
      {
        heading: 'Exceptional service that feels like home',
        blocks: [
          {
            kind: 'text',
            text: 'ABM provides courteous, attentive service to residents and their guests. From secure parking operations to valet attendants who know residents by name, we offer a boutique-style experience that adds everyday convenience and peace of mind.',
          },
        ],
        image: 'v2-residential-exceptional-service.webp',
      },
      {
        heading: 'Enhancing resident experience, one parking space at a time',
        blocks: [
          {
            kind: 'text',
            text: 'Our residential parking solutions are built to support the unique needs of modern communities with a range of offerings that include:',
          },
          { kind: 'subheading', text: 'Valet Parking Services' },
          {
            kind: 'list',
            items: [
              'Professional, uniformed attendants trained in hospitality and safe vehicle handling',
              'Personalized service that recognizes residents and greets guests by name',
              'Fast and efficient retrieval, reducing wait times and enhancing satisfaction',
            ],
          },
          { kind: 'subheading', text: 'Self-Parking Management' },
          {
            kind: 'list',
            items: [
              'Oversight of resident parking garages and lots',
              'Clear signage, optimized space utilization, and access control enforcement',
              'Cleanliness, maintenance, and safety patrols to ensure a high standard of operation',
            ],
          },
          { kind: 'subheading', text: 'Customer Experience Enhancements' },
          {
            kind: 'list',
            items: [
              'Custom greeting scripts to ensure a professional, branded experience',
              'Resident surveys and feedback tools to continuously improve service delivery',
            ],
          },
          {
            kind: 'text',
            text: 'With a nationwide presence and decades of experience in residential parking, ABM is the partner communities trust to deliver a seamless, secure, and service-forward parking experience—day in and day out.',
          },
        ],
        image: 'v2-residential-enhancing-resident.webp',
      },
    ],
  },
  {
    slug: 'retail',
    title: 'Retail',
    summary:
      'At ABM, we know the parking experience sets the tone for every visit. The customer journey begins before shoppers even enter the store. ABM Parking plays a vital role in shaping that first impression, delivering parking solutions that are fast, convenient, and customer-focused—ultimately enhancing the entire retail experience.',
    icon: 'industry-retail.svg',
    heroImage: 'v2-retail-header.webp',
    cardImage: 'v2-retail-seamless-parking.webp',
    sections: [
      {
        heading: 'Seamless parking that keeps shoppers coming back',
        blocks: [
          { kind: 'subheading', text: 'Valet Parking Services' },
          {
            kind: 'list',
            items: [
              'Professional, uniformed attendants trained in hospitality and safe vehicle handling',
              'Personalized service that recognizes residents and greets guests by name',
              'Fast and efficient retrieval, reducing wait times and enhancing satisfaction',
            ],
          },
        ],
        image: 'v2-retail-seamless-parking.webp',
      },
      {
        heading: 'We leverage cutting-edge parking technology to create a frictionless journey:',
        blocks: [
          {
            kind: 'list',
            items: [
              'License Plate Recognition (LPR): Fast, ticketless entry and exit',
              'Digital Payment Systems: Contactless, mobile, and app-based options',
              'Real-Time Availability: Sensors and signage guide shoppers to open spaces in seconds',
              'Automated guidance systems to reduce congestion and improve flow',
            ],
          },
          { kind: 'subheading', text: 'We continuously improve service with:' },
          {
            kind: 'list',
            items: [
              'Customer feedback tools',
              'Quarterly performance reviews',
              'Real-time reporting and analytics',
            ],
          },
          {
            kind: 'text',
            text: 'Connect with us to learn how we can elevate your customer experience from the curb in.',
          },
        ],
        image: 'shutterstock-1577047654.webp',
      },
      {
        heading: 'Case Study: Horton Plaza',
        blocks: [
          {
            kind: 'text',
            text: 'ABM transformed a 2,189-space garage in Downtown San Diego through strategic technology upgrades, real-time analytics, and hands-on onsite management. With dynamic pricing, license plate recognition, and marketing partnerships with major venues like Petco Park and the Convention Center, ABM expanded the garage’s customer base and boosted convenience.',
          },
        ],
        image: 'v2-retail-horton-plaza.webp',
        ctas: [caseStudy('https://brandcenter.abm.com/share/0DC178BF-3195-4559-B25053B108F63CF8/')],
      },
    ],
  },
  {
    slug: 'stadium-events',
    title: 'Stadium & Events',
    summary:
      'ABM delivers seamless parking and transportation management for stadiums, arenas, and large-scale events. From traffic flow planning and valet services to contactless payments and real-time occupancy tools, we ensure efficient, guest-friendly experiences from arrival to departure. With a focus on safety, speed, and service, ABM keeps events running smoothly—no matter the size or crowd.',
    icon: 'industry-stadium-events.svg',
    heroImage: 'v2-stadium-events-header.webp',
    cardImage: 'v2-stadium-seamless-experiences.webp',
    sections: [
      {
        heading: 'Ensuring seamless experiences',
        blocks: [
          {
            kind: 'text',
            text: 'ABM Parking has established itself as a leader in providing parking solutions for stadiums and arenas, ensuring a seamless experience for event attendees. ABM treats visitors as their own, aiming to create an unforgettable customer experience. ABM’s expertise in stadium and arena event planning, traffic flow, use of technology, and commitment to customer satisfaction sets the standard for parking management at large-capacity events.',
          },
        ],
        image: 'v2-stadium-seamless-experiences.webp',
      },
      {
        heading: 'Digital tools that streamline the event parking journey',
        blocks: [
          {
            kind: 'text',
            text: 'ABM leverages a range of digital tools to enhance the parking experience for event attendees. These tools help reduce confusion and streamline the process, making parking at large events more efficient and user-friendly:',
          },
          {
            kind: 'list',
            items: [
              'Contactless Payments: Our contactless payment options provide a quick and secure way for attendees to pay for parking, minimizing physical contact and speeding up transactions.',
              'Parking Apps: Our parking apps provide real-time information on parking availability, directions to parking facilities, and other useful features that enhance the overall experience for eventgoers.',
              'License Plate Readers: These readers are part of ABM’s touchless technology suite, allowing for quick and efficient entry and exit from parking facilities.',
            ],
          },
          {
            kind: 'text',
            text: 'By combining strategic planning, advanced technologies, and a focus on customer experience, ABM effectively manages parking for large events, ensuring a seamless and enjoyable experience for all attendees.',
          },
        ],
        image: 'v2-stadium-digital-tools.webp',
      },
      {
        heading: 'Case Study: Chicago’s Historic Navy Pier',
        blocks: [
          {
            kind: 'text',
            text: 'After 25 years, the Navy Pier team sought a new parking management partner to boost innovation and data usage for increased revenue and profitability. Since 2018, ABM has provided parking management, janitorial, and valet services. Navy Pier, a historic destination, welcomes nearly nine million guests annually and hosts various events and attractions.',
          },
        ],
        image: 'v2-stadium-navy-pier.webp',
        ctas: [caseStudy('https://brandcenter.abm.com/share/F9606D73-DC78-43F9-BAFA0336062CE53F/')],
      },
      {
        heading: 'Case Study: San Francisco Giants',
        blocks: [
          {
            kind: 'text',
            text: 'ABM has partnered with the San Francisco Giants to manage parking operations for Oracle Park for three years. This partnership expands our presence in San Francisco and adds to our extensive experience managing parking facilities, including at other sports venues. We are managing three parking facilities at Oracle Park and aim to enhance the fan, player, and employee experience through our parking management services. The partnership will leverage our expertise in parking operations and innovative technologies to optimize parking facilities and provide a superior fan experience at Oracle Park.',
          },
        ],
        image: 'v2-stadium-sf-giants.webp',
        ctas: [caseStudy('https://brandcenter.abm.com/share/4EB9AAE6-B0C5-443D-9A43386EDE14E844/')],
      },
    ],
  },
];
